export type Direction = "up" | "down" | "left" | "right";
export type GameStatus = "playing" | "paused" | "gameover";

export type Point = {
  x: number;
  y: number;
};

export type GameState = {
  rows: number;
  cols: number;
  snake: Point[];
  direction: Direction;
  food: Point | null;
  score: number;
  status: GameStatus;
};

export type GameConfig = {
  rows: number;
  cols: number;
  initialLength?: number;
};

const DEFAULT_INITIAL_LENGTH = 3;

export function createInitialState(
  { rows, cols, initialLength = DEFAULT_INITIAL_LENGTH }: GameConfig,
  rng: () => number,
): GameState {
  const clampedLength = Math.max(2, Math.min(initialLength, cols));
  const startX = Math.floor(cols / 2) - 1;
  const startY = Math.floor(rows / 2);
  const snake: Point[] = Array.from({ length: clampedLength }, (_, index) => ({
    x: startX - index,
    y: startY,
  }));

  const food = spawnFood({ rows, cols }, snake, rng);

  return {
    rows,
    cols,
    snake,
    direction: "right",
    food,
    score: 0,
    status: "playing",
  };
}

export function stepGame(
  state: GameState,
  requestedDirection: Direction | null,
  rng: () => number,
): GameState {
  if (state.status !== "playing") {
    return state;
  }

  const direction = getNextDirection(state.direction, requestedDirection);
  const nextHead = movePoint(state.snake[0], direction);
  const hitWall =
    nextHead.x < 0 ||
    nextHead.x >= state.cols ||
    nextHead.y < 0 ||
    nextHead.y >= state.rows;

  if (hitWall) {
    return {
      ...state,
      direction,
      status: "gameover",
    };
  }

  const willEat = state.food ? isSamePoint(nextHead, state.food) : false;
  const bodyToCheck = willEat ? state.snake : state.snake.slice(0, -1);
  const hitSelf = bodyToCheck.some((segment) => isSamePoint(segment, nextHead));

  if (hitSelf) {
    return {
      ...state,
      direction,
      status: "gameover",
    };
  }

  const nextSnake = [nextHead, ...state.snake];
  if (!willEat) {
    nextSnake.pop();
  }

  let nextFood = state.food;
  let nextStatus: GameStatus = state.status;
  let nextScore = state.score;

  if (willEat) {
    nextScore += 1;
    nextFood = spawnFood({ rows: state.rows, cols: state.cols }, nextSnake, rng);
    if (!nextFood) {
      nextStatus = "gameover";
    }
  }

  return {
    ...state,
    snake: nextSnake,
    direction,
    food: nextFood,
    score: nextScore,
    status: nextStatus,
  };
}

export function togglePause(state: GameState): GameState {
  if (state.status === "gameover") {
    return state;
  }
  return {
    ...state,
    status: state.status === "paused" ? "playing" : "paused",
  };
}

export function getNextDirection(
  current: Direction,
  requested: Direction | null,
): Direction {
  if (!requested) {
    return current;
  }
  if (isOpposite(current, requested)) {
    return current;
  }
  return requested;
}

export function spawnFood(
  grid: { rows: number; cols: number },
  snake: Point[],
  rng: () => number,
): Point | null {
  const occupied = new Set(snake.map((segment) => toKey(segment)));
  const available: Point[] = [];

  for (let y = 0; y < grid.rows; y += 1) {
    for (let x = 0; x < grid.cols; x += 1) {
      if (!occupied.has(toKey({ x, y }))) {
        available.push({ x, y });
      }
    }
  }

  if (available.length === 0) {
    return null;
  }

  const index = Math.floor(rng() * available.length);
  return available[Math.min(index, available.length - 1)];
}

export function movePoint(point: Point, direction: Direction): Point {
  switch (direction) {
    case "up":
      return { x: point.x, y: point.y - 1 };
    case "down":
      return { x: point.x, y: point.y + 1 };
    case "left":
      return { x: point.x - 1, y: point.y };
    case "right":
      return { x: point.x + 1, y: point.y };
  }
}

export function isSamePoint(a: Point, b: Point): boolean {
  return a.x === b.x && a.y === b.y;
}

export function toKey(point: Point): string {
  return `${point.x}:${point.y}`;
}

function isOpposite(current: Direction, next: Direction): boolean {
  return (
    (current === "up" && next === "down") ||
    (current === "down" && next === "up") ||
    (current === "left" && next === "right") ||
    (current === "right" && next === "left")
  );
}
